import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronRight, ExternalLink, Mic } from 'lucide-react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useNavigation } from '../contexts/NavigationContext';

interface SearchResult {
  area: string;
  path: string;
  fullPath: string;
  url: string;
  description: string;
}

interface GlobalSearchProps {
  onClose?: () => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const currentSearchRef = useRef<string>(''); // Track current search to prevent race conditions
  const recognizerRef = useRef<SpeechSDK.SpeechRecognizer | null>(null);
  const confirmedTextRef = useRef<string>(''); // Track confirmed transcription text
  const speechTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Timeout to auto-stop after silence
  const [isRecording, setIsRecording] = useState(false);
  const [speechError, setSpeechError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  // Focus input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showResults) {
        handleClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showResults]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    if (showResults) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showResults]);

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setShowResults(false);
      currentSearchRef.current = '';
      return;
    }

    // Store the current search query to check against when response arrives
    currentSearchRef.current = searchQuery;

    // Don't show results dropdown until we have data
    setLoading(true);
    setShowResults(false); // Hide results while loading
    setResults([]); // Clear previous results

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: searchQuery,
          language: 'en',
          forceIntent: 'global_search' // Force global_search intent
        })
      });

      const data = await response.json();

      // Check if this response is still relevant (user might have typed more)
      if (currentSearchRef.current !== searchQuery) {
        console.log('[GlobalSearch] Ignoring stale response for:', searchQuery);
        return;
      }

      console.log('[GlobalSearch] Full response:', JSON.stringify(data, null, 2));

      if (data.success) {
        // Check multiple possible response structures for global_search results
        let results: SearchResult[] = [];
        
        // Check for global_search results in various nested structures
        // MCP route structure: data.data.result.data.results (where result.data comes from handleGlobalSearch)
        if (data.data?.result?.data?.results) {
          // Nested: data.data.result.data.results (most likely structure)
          results = data.data.result.data.results;
        } else if (data.data?.result?.results) {
          // Alternative: data.data.result.results
          results = data.data.result.results;
        } else if (data.data?.data?.results) {
          // Alternative: data.data.data.results
          results = data.data.data.results;
        } else if (data.data?.results) {
          // Direct: data.data.results
          results = data.data.results;
        } else if (data.result?.results) {
          // Another alternative: data.result.results
          results = data.result.results;
        } else if (data.result?.data?.results) {
          // Another alternative: data.result.data.results
          results = data.result.data.results;
        }
        
        console.log('[GlobalSearch] Parsed results:', results);
        console.log('[GlobalSearch] Results count:', results.length);
        
        // Double-check the search query hasn't changed while we were processing
        if (currentSearchRef.current !== searchQuery) {
          console.log('[GlobalSearch] Query changed during processing, ignoring results');
          return;
        }
        
        // Only show results if we have them and the query hasn't changed
        if (Array.isArray(results) && results.length > 0) {
          setResults(results);
          setShowResults(true); // Show results dropdown only after we have data
        } else {
          setResults([]);
          setShowResults(true); // Show "no results" message
        }
      } else {
        setResults([]);
        setShowResults(true); // Show "no results" message
      }
      
      // Dispatch event for AI Architect Panel AFTER MCP call completes (regardless of results)
      // This allows the panel to show the MCP execution process
      const isDevelopment = process.env.NODE_ENV === 'development';
      if (isDevelopment) {
        // Use requestAnimationFrame to ensure the event is dispatched after state updates
        requestAnimationFrame(() => {
          window.dispatchEvent(new CustomEvent('globalSearchTrigger', {
            detail: {
              message: searchQuery,
              language: 'en'
            }
          }));
        });
      }
    } catch (error) {
      console.error('Global search error:', error);
      // Only update UI if this is still the current search
      if (currentSearchRef.current === searchQuery) {
        setResults([]);
        setShowResults(true); // Show "no results" message even on error
      }
    } finally {
      // Only update loading state if this is still the current search
      if (currentSearchRef.current === searchQuery) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    
    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Clear results immediately when typing
    if (!value.trim()) {
      setResults([]);
      setShowResults(false);
      currentSearchRef.current = '';
      return;
    }
    
    // Only search after user stops typing for 1.5 seconds (to save API costs)
    // User can also press Enter or click search button for immediate search
    debounceTimeoutRef.current = setTimeout(() => {
      if (value.trim()) {
        handleSearch(value);
      }
    }, 1500); // Increased to 1.5 seconds to reduce API calls
  };

  const handleResultClick = (result: SearchResult) => {
    // Navigate to the URL (NavigationContext will handle path normalization)
    navigate(result.url);
    
    // Close the search dropdown
    handleClose();
  };

  const handleClose = () => {
    setQuery('');
    setResults([]);
    setShowResults(false);
    onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && query.trim()) {
      // Cancel any pending debounced search
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Immediately search on Enter (user explicitly requested search)
      handleSearch(query);
    }
  };

  const handleSearchClick = () => {
    if (query.trim()) {
      // Cancel any pending debounced search
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Immediately search on button click (user explicitly requested search)
      handleSearch(query);
    }
  };

  const startAzureSpeechRecognition = async () => {
    // If already recording, stop it
    if (isRecording) {
      stopAzureSpeechRecognition();
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Get Azure Speech credentials from backend
      const response = await fetch(`${apiUrl}/api/ai/azure-speech-token`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get Azure Speech credentials');
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Azure Speech not configured');
      }

      const { key, region } = data.data;

      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(key, region);
      speechConfig.speechRecognitionLanguage = 'en-US';
      
      // Enable better accuracy features
      speechConfig.enableDictation();
      speechConfig.requestWordLevelTimestamps();
      speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;
      
      // Enable automatic punctuation and capitalization
      speechConfig.setServiceProperty('punctuation', 'explicit', SpeechSDK.ServicePropertyChannel.UriQueryParameter);
      speechConfig.setServiceProperty('format', 'detailed', SpeechSDK.ServicePropertyChannel.UriQueryParameter);
      
      const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
      const recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

      recognizerRef.current = recognizer;
      
      // Reset confirmed text when starting new recognition
      confirmedTextRef.current = '';

      // Real-time recognition handler (interim results)
      recognizer.recognizing = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizingSpeech && e.result.text) {
          // Update query: confirmed text + current interim result (replaces previous interim)
          const displayText = confirmedTextRef.current 
            ? `${confirmedTextRef.current} ${e.result.text}` 
            : e.result.text;
          setQuery(displayText);
        }
      };

      // Final recognition handler (confirmed results)
      recognizer.recognized = (_s, e) => {
        if (e.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
          // Use display format for best readability (includes punctuation & capitalization)
          const recognizedText = (e.result as any).privJson?.DisplayText || e.result.text;
          
          if (recognizedText) {
            // Update confirmed text
            confirmedTextRef.current = confirmedTextRef.current 
              ? `${confirmedTextRef.current} ${recognizedText}` 
              : recognizedText;
            
            // Update query with confirmed text only (no interim)
            setQuery(confirmedTextRef.current);
            
            // Clear any existing timeout
            if (speechTimeoutRef.current) {
              clearTimeout(speechTimeoutRef.current);
            }
            
            // Set timeout to auto-stop after 2 seconds of silence
            speechTimeoutRef.current = setTimeout(() => {
              console.log('[GlobalSearch] Auto-stopping speech recognition after silence');
              stopAzureSpeechRecognition();
              
              // Auto-search when speech ends
              if (confirmedTextRef.current.trim()) {
                handleSearch(confirmedTextRef.current.trim());
              }
            }, 2000); // 2 seconds of silence before stopping
          }
        } else if (e.result.reason === SpeechSDK.ResultReason.NoMatch) {
          console.log('No speech recognized');
        }
      };

      recognizer.canceled = (_s, e) => {
        console.error('Recognition canceled:', e.errorDetails);
        setIsRecording(false);
        setSpeechError('Speech recognition stopped: ' + e.errorDetails);
        stopAzureSpeechRecognition();
      };
      
      recognizer.sessionStarted = (_s, e) => {
        console.log('Speech session started:', e.sessionId);
        setIsRecording(true);
        setSpeechError(null);
      };
      
      recognizer.sessionStopped = (_s, e) => {
        console.log('Speech session stopped:', e.sessionId);
        setIsRecording(false);
        
        // Clear timeout if session stopped
        if (speechTimeoutRef.current) {
          clearTimeout(speechTimeoutRef.current);
          speechTimeoutRef.current = null;
        }
      };

      await recognizer.startContinuousRecognitionAsync();

    } catch (err) {
      console.error('Azure Speech error:', err);
      setSpeechError(err instanceof Error ? err.message : 'Failed to start speech recognition');
      setIsRecording(false);
    }
  };

  const stopAzureSpeechRecognition = () => {
    // Clear any pending timeout
    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
    
    if (recognizerRef.current) {
      recognizerRef.current.stopContinuousRecognitionAsync(() => {
        recognizerRef.current?.close();
        recognizerRef.current = null;
        setIsRecording(false);
        setSpeechError(null);
        // Keep confirmed text, just stop recording
      });
    } else {
      setIsRecording(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear timeout
      if (speechTimeoutRef.current) {
        clearTimeout(speechTimeoutRef.current);
      }
      stopAzureSpeechRecognition();
    };
  }, []);

  return (
    <>
      <div className="relative flex-1 max-w-2xl">
        <div className="relative flex items-center">
          <Search className="absolute left-3 text-gray-400 w-5 h-5 pointer-events-none" />
          <input
            ref={searchInputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={t('common.globalSearch.placeholder')}
            className={`w-full pl-10 ${query ? 'pr-28' : 'pr-20'} py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
            aria-label={t('common.globalSearch.placeholder')}
          />
          {loading ? (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '160ms', animationDuration: '1.4s' }}></div>
              <div className="w-1.5 h-1.5 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '320ms', animationDuration: '1.4s' }}></div>
            </div>
          ) : (
            <>
              {/* Microphone button - always visible */}
              <button
                onClick={startAzureSpeechRecognition}
                className={`absolute right-10 top-1/2 transform -translate-y-1/2 p-1.5 rounded-full transition-colors ${
                  isRecording 
                    ? 'text-error-600 dark:text-error-400 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 animate-pulse' 
                    : 'text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-100 dark:hover:bg-gray-600'
                }`}
                aria-label={isRecording ? t('common.globalSearch.stopRecording') : t('common.globalSearch.startRecording')}
                title={isRecording ? t('common.globalSearch.stopRecording') : t('common.globalSearch.startRecording')}
              >
                <Mic className={`w-4 h-4 ${isRecording ? 'animate-pulse' : ''}`} />
              </button>
              
              {/* Search button - only show when there's a query */}
              {query && (
                <button
                  onClick={handleSearchClick}
                  className="absolute right-20 top-1/2 transform -translate-y-1/2 p-1 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                  aria-label="Search"
                  title="Search (or press Enter)"
                >
                  <Search className="w-4 h-4" />
                </button>
              )}
              
              {/* Close button - only show when there's a query */}
              {query && (
                <button
                  onClick={handleClose}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  aria-label={t('common.close')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </>
          )}
        </div>

        {/* Speech Error Message */}
        {speechError && (
          <div className="absolute top-full left-0 right-0 mt-2 p-2 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg text-sm text-error-600 dark:text-error-400 z-50">
            {speechError}
          </div>
        )}

        {/* Results Dropdown */}
        {showResults && (
          <div
            ref={resultsRef}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto"
          >
            {loading ? (
              <div className="p-6 flex flex-col items-center justify-center">
                <div className="flex items-center space-x-1 mb-2">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }}></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '160ms', animationDuration: '1.4s' }}></div>
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-bounce" style={{ animationDelay: '320ms', animationDuration: '1.4s' }}></div>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {t('common.globalSearch.searching')}
                </span>
              </div>
            ) : results.length > 0 ? (
              <div className="p-2">
                {results.map((result, index) => (
                  <button
                    key={index}
                    onClick={() => handleResultClick(result)}
                    className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className="text-xs font-semibold text-primary-600 dark:text-primary-400">
                            {result.area}
                          </span>
                          {result.path !== result.fullPath && (
                            <>
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {result.path}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                          {result.fullPath}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {result.description}
                        </div>
                      </div>
                      <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-primary-500 flex-shrink-0 ml-2" />
                    </div>
                  </button>
                ))}
              </div>
            ) : query.trim() ? (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                        {t('common.globalSearch.noResults')}
                      </div>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
};

